import React from 'react';
import { View, StyleSheet } from 'react-native';

import { CarouselErrorBoundary } from './CarouselErrorBoundary';
import { AudioRenderer } from './renderers/AudioRenderer';
import { ImageRenderer } from './renderers/ImageRenderer';
import { PDFRenderer } from './renderers/PDFRenderer';
import { TextRenderer } from './renderers/TextRenderer';
import { UnsupportedRenderer } from './renderers/UnsupportedRenderer';
import { VideoRenderer } from './renderers/VideoRenderer';
import { useResponsive } from '../../../hooks/useResponsive';

import type { CarouselMedia } from './carouselMedia';

interface CarouselItemProps {
  item: CarouselMedia;
  fileUrl?: string;
  isLoading: boolean;
  hasError: boolean;
  isActive: boolean;
  /** Explicit pixel height — required on web where horizontal FlatList cells have height:auto */
  height?: number;
  /** Save an item the app cannot render; absent, the unsupported page offers no Download. */
  onDownload?: () => void | Promise<void>;
  /** One source pixel per screen pixel. The modal owns this so a list remount keeps it. */
  showSharpPixels?: boolean;
  onToggleSharpPixels?: () => void;
}

export const CarouselItem: React.FC<CarouselItemProps> = ({
  item,
  fileUrl,
  isLoading,
  hasError,
  isActive,
  height,
  onDownload,
  showSharpPixels,
  onToggleSharpPixels,
}) => {
  const { mimeType, thumbnailUri: thumbnailUrl, name } = item;
  const { dimensions } = useResponsive();
  const screenWidth = dimensions.width;

  const renderContent = () => {
    // ⚠️ `previewable: false` BEFORE THE TYPE SWITCH (GitLab #1539). A photo
    // the app cannot draw on every platform (an `image/tiff`) still carries an
    // image type; routing it to ImageRenderer would spin forever, because the
    // loader never asks the source for it.
    if (item.previewable === false) {
      return (
        <UnsupportedRenderer
          fileName={name}
          mimeType={mimeType}
          fileSizeBytes={item.sizeBytes}
          onDownload={onDownload}
        />
      );
    }

    if (mimeType?.startsWith('image/')) {
      return (
        <ImageRenderer
          fileUrl={fileUrl}
          thumbnailUrl={thumbnailUrl}
          isLoading={isLoading}
          hasError={hasError}
          fileName={name}
          showSharpPixels={showSharpPixels}
          onToggleSharpPixels={onToggleSharpPixels}
        />
      );
    }

    if (mimeType?.startsWith('video/')) {
      return (
        <VideoRenderer
          fileUrl={fileUrl}
          thumbnailUrl={thumbnailUrl}
          isLoading={isLoading}
          hasError={hasError}
          fileName={name}
          isActive={isActive}
        />
      );
    }

    if (mimeType?.startsWith('audio/')) {
      return (
        <AudioRenderer
          fileUrl={fileUrl}
          isLoading={isLoading}
          hasError={hasError}
          fileName={name}
          isActive={isActive}
        />
      );
    }

    if (mimeType === 'application/pdf') {
      return (
        <PDFRenderer
          fileUrl={fileUrl}
          isLoading={isLoading}
          hasError={hasError}
          fileName={name}
        />
      );
    }

    if (mimeType === 'text/plain') {
      return (
        <TextRenderer
          fileUrl={fileUrl}
          isLoading={isLoading}
          hasError={hasError}
          fileName={name}
        />
      );
    }

    return (
      <UnsupportedRenderer
        fileName={name}
        mimeType={mimeType}
        fileSizeBytes={item.sizeBytes}
        onDownload={onDownload}
      />
    );
  };

  return (
    <View
      style={[
        styles.page,
        { width: screenWidth },
        height ? { height } : undefined,
      ]}
    >
      <CarouselErrorBoundary fileName={name}>
        {renderContent()}
      </CarouselErrorBoundary>
    </View>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: 'transparent',
  },
});
